import { Injectable, Injector, OnDestroy } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AppConsts } from '@shared/AppConsts';
import { AppComponentBase } from '@shared/common/app-component-base';
import { Subscription } from 'rxjs';
import { ITabMenuItem } from '../mazars-common/components/mazars-tab-menu/tab-menu-interfaces';
import { ActionIcon } from '@app/modules/mazars-common/components/mazars-actions-dropdown-menu/action-icon';
import { Pillar2Permissions } from './pillar2-permissions';
import { JurisdictionRuleStatusType, ObligationStatus, ProjectDto, SafeHarbourCheckResultDto, SafeHarbourGroupType } from './pillar2-proxies';
import { SubmissionProcessStatePermissions } from './submissions/submission-process-flow/submission-process-state-permissions';

@Injectable()
export abstract class Pillar2ComponentBase extends AppComponentBase implements OnDestroy {
    subscriptions: Subscription[] = [];
    pillar2TabMenuItems: ITabMenuItem[];
    projectTabMenuItems: ITabMenuItem[];
    projectAdditionalTabMenuItems: ITabMenuItem[] = [];
    submissionTabMenuItems: ITabMenuItem[];
    submissionAdditionalTabMenuItems: ITabMenuItem[] = [];

    public activatedRoute: ActivatedRoute;

    public absoluteModuleRootPath = '/app/pillar2';
    public absoluteModuleRootPathProjects: string = this.absoluteModuleRootPath + '/projects';
    public absoluteModuleRootPathProjectsCreate: string = this.absoluteModuleRootPathProjects + '/create';
    public absoluteModuleRootPathSubmissions: string = this.absoluteModuleRootPath + '/submissions';
    public absoluteModuleRootPathSubmissionsCreate: string = this.absoluteModuleRootPathProjects + '/submissions/create';

    public absoluteModuleRootPathAdminSettings: string = this.absoluteModuleRootPath + '/admin-settings';

    protected fb: FormBuilder;

    constructor(injector: Injector) {
        super(injector);
        this.localizationSourceName = AppConsts.localization.pillar2SourceName;
        this.activatedRoute = injector.get(ActivatedRoute);
        this.fb = injector.get(FormBuilder);
        this.pillar2TabMenuItems = [
            {
                id: 'mi-projects',
                label: this.l('Projects'),
                routerLink: this.absoluteModuleRootPathProjects,
            },
            {
                id: 'mi-submissions',
                label: this.l('Submissions'),
                routerLink: this.absoluteModuleRootPathSubmissions,
                hidden: !this.permission.isGranted(SubmissionProcessStatePermissions.ViewSubmission),
            },
            {
                id: 'pillar2-settings',
                label: '<i aria-hidden="true" class="fas fa-cog"></i>',
                labelAsHtml: true,
                routerLink: this.absoluteModuleRootPathAdminSettings,
                hidden: !this.permission.isGranted(Pillar2Permissions.Admin),
            },
        ];
        this.projectTabMenuItems = [
            {
                id: 'p-general',
                label: this.l('General'),
                icon: ActionIcon.CheckCircle,
                routerLink: `${this.absoluteModuleRootPathProjects}/${this.activatedRoute.snapshot.params.id}/general`,
                disabled: false,
            },
            {
                id: 'p-import-export',
                label: this.l('ImportAndExport'),
                icon: ActionIcon.Lock,
                routerLink: `${this.absoluteModuleRootPathProjects}/${this.activatedRoute.snapshot.params.id}/import-export`,
                disabled: true,
            },
            {
                id: 'p-scoping',
                label: this.l('Scoping'),
                icon: ActionIcon.Lock,
                routerLink: `${this.absoluteModuleRootPathProjects}/${this.activatedRoute.snapshot.params.id}/scoping`,
                disabled: true,
            },
            {
                id: 'p-jurisdictions',
                label: this.l('Jurisdictions'),
                icon: ActionIcon.Lock,
                routerLink: `${this.absoluteModuleRootPathProjects}/${this.activatedRoute.snapshot.params.id}/jurisdictions`,
                disabled: true,
            },
            {
                id: 'p-currencies',
                label: this.l('Currencies'),
                icon: ActionIcon.Lock,
                routerLink: `${this.absoluteModuleRootPathProjects}/${this.activatedRoute.snapshot.params.id}/currencies`,
                disabled: true,
            },
            {
                id: 'p-access-management',
                label: this.l('AccessManagementName'),
                icon: ActionIcon.Lock,
                routerLink: `${this.absoluteModuleRootPathProjects}/${this.activatedRoute.snapshot.params.id}/access-management`,
                disabled: true,
                hidden: true,
            },
            {
                id: 'p-previous-reporting-years',
                label: this.l('PreviousReportingYears'),
                icon: ActionIcon.Lock,
                routerLink: `${this.absoluteModuleRootPathProjects}/${this.activatedRoute.snapshot.params.id}/previous-reporting-years`,
                disabled: true,
                hidden: true,
            },
            {
                id: 'p-checks',
                label: this.l('SafeHarbourChecks'),
                icon: ActionIcon.Lock,
                routerLink: `${this.absoluteModuleRootPathProjects}/${this.activatedRoute.snapshot.params.id}/checks`,
                disabled: true,
            },
            {
                id: 'p-full-scope-calc',
                label: this.l('FullScopeCalculation'),
                icon: ActionIcon.Lock,
                routerLink: `${this.absoluteModuleRootPathProjects}/${this.activatedRoute.snapshot.params.id}/full-scope-calculation`,
                ignoreQueryParams: true,
                disabled: true,
                hidden: false,
            },
            {
                id: 'p-utpr',
                label: this.l('UtprTabName'),
                icon: ActionIcon.Lock,
                routerLink: `${this.absoluteModuleRootPathProjects}/${this.activatedRoute.snapshot.params.id}/utpr`,
                disabled: true,
                hidden: true,
            },
            {
                id: 'p-report',
                label: this.l('Reports'),
                icon: ActionIcon.Lock,
                routerLink: `${this.absoluteModuleRootPathProjects}/${this.activatedRoute.snapshot.params.id}/report`,
                disabled: true,
                hidden: true,
            },
            {
                id: 'p-obligations',
                label: this.l('FilingObligations'),
                icon: ActionIcon.CheckCircle,
                routerLink: `${this.absoluteModuleRootPathProjects}/${this.activatedRoute.snapshot.params.id}/obligations`,
                disabled: true,
                hidden: true,
            },
            {
                id: 'p-submissions',
                label: this.l('Submissions'),
                icon: ActionIcon.CheckCircle,
                routerLink: `${this.absoluteModuleRootPathProjects}/${this.activatedRoute.snapshot.params.id}/submissions`,
                disabled: true,
                hidden: true,
            },
            {
                id: 'p-insights',
                label: this.l('Insights'),
                icon: ActionIcon.CheckCircle,
                routerLink: `${this.absoluteModuleRootPathProjects}/${this.activatedRoute.snapshot.params.id}/insights`,
                disabled: true,
            },
            {
                id: 'p-attachments',
                label: this.l('Attachments'),
                icon: ActionIcon.CheckCircle,
                routerLink: `${this.absoluteModuleRootPathProjects}/${this.activatedRoute.snapshot.params.id}/attachments`,
                disabled: true,
            },
        ];
        this.projectAdditionalTabMenuItems = [
            {
                id: 'p-central-notifications',
                icon: ActionIcon.CheckList,
                routerLink: `${this.absoluteModuleRootPathProjects}/${this.activatedRoute.snapshot.params.id}/notifications`,
                disabled: true,
                tooltip: this.ld('Notifications'),
            },
            {
                id: 'p-communications',
                icon: ActionIcon.Comment,
                routerLink: `${this.absoluteModuleRootPathProjects}/${this.activatedRoute.snapshot.params.id}/communications`,
                disabled: true,
                showIconCounter: true,
                tooltip: this.ld('Channels_Tooltip'),
            },
        ];

        this.submissionTabMenuItems = [
            {
                id: 'p-general',
                label: this.l('General'),
                icon: ActionIcon.CheckCircle,
                routerLink: `${this.absoluteModuleRootPathProjects}/${this.activatedRoute.snapshot.params.id}/general`,
                disabled: false,
            },
            {
                id: 'p-submission-form',
                label: this.l('SubmissionForm'),
                icon: ActionIcon.Lock,
                routerLink: `${this.absoluteModuleRootPathProjects}/${this.activatedRoute.snapshot.params.id}/submission-form`,
                disabled: true,
            },
        ];

        this.submissionAdditionalTabMenuItems = [
            {
                id: 'p-history',
                icon: ActionIcon.History,
                routerLink: `${this.absoluteModuleRootPathProjects}/${this.activatedRoute.snapshot.params.id}/history`,
                disabled: true,
                tooltip: this.ld('StateHistory_Tooltip'),
            },
        ];
    }

    setTabIcon(tabName: string, canEditData: boolean) {
        let menuItem = this.projectTabMenuItems.find((mi) => mi.id === tabName);
        if (menuItem) {
            menuItem.icon = canEditData ? ActionIcon.CheckCircle : ActionIcon.Lock;
        }
    }

    setTabIconSubmission(tabName: string, canEditData: boolean) {
        let menuItem = this.submissionTabMenuItems.find((mi) => mi.id === tabName);
        if (menuItem) {
            menuItem.icon = canEditData ? ActionIcon.CheckCircle : ActionIcon.Lock;
        }
    }

    setIsVisible(tabName: string, isVisible: boolean) {
        let menuItem = this.projectTabMenuItems.find((mi) => mi.id === tabName);
        if (menuItem) {
            menuItem.hidden = !isVisible;
        }
    }

    isPreviousReportingYearsAvailable(project: ProjectDto | null | undefined): boolean {
        const fiscalYear = project?.specifics?.fiscalYearPeriod?.[0]?.year;
        const globeRulesApplicableAsOf = project?.specifics?.globeRulesApplicableAsOf;
        return fiscalYear != null && globeRulesApplicableAsOf != null && fiscalYear > globeRulesApplicableAsOf;
    }

    localizer: (key: string) => string = (key: string) => this.l(key);

    ngOnDestroy(): void {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
        this.onDestroy();
    }

    ld(key: string): string {
        const source = abp.localization.getSource(AppConsts.localization.defaultLocalizationSourceName);
        return source(key);
    }

    getReasonsOfCheckResult(checkResult: SafeHarbourCheckResultDto): string {
        let resultReasons = checkResult?.resultReasons;
        let finalReason = '';
        resultReasons?.forEach((reason) => {
            finalReason = finalReason + reason + '\n';
        });
        return finalReason;
    }

    getSubgroupDisplayName(safeHarbourGroupType: any, number: any) {
        switch (safeHarbourGroupType) {
            case SafeHarbourGroupType.StandardPlus:
                return this.l('GroupedSubgroupType_StandardPlus');
            case SafeHarbourGroupType.JointVenture:
                return this.l('GroupedSubgroupType_JointVenture') + (number ? number : null);
            case SafeHarbourGroupType.NotConsidered:
                return this.l('GroupedSubgroupType_NotConsidered');
        }
    }

    getObligationStatusIcon(status?: ObligationStatus): string {
        switch (status) {
            case ObligationStatus.FilingCompleted:
                return 'pi pi-file-check mz-green';
            case ObligationStatus.CheckFilingObligation:
                return 'pi pi-question-circle mz-red';
            case ObligationStatus.FilingNotRequired:
                return 'pi pi-file-excel mz-gray';
            case ObligationStatus.FilingRequired:
                return 'pi pi-exclamation-circle mz-orange';
        }
    }

    getJurisdictionRuleStatusIcon(status?: JurisdictionRuleStatusType): string {
        switch (status) {
            case JurisdictionRuleStatusType.Implemented:
                return 'pi pi-check-circle mz-green';
            case JurisdictionRuleStatusType.NotImplemented:
                return 'pi pi-times-circle mz-red';
            case JurisdictionRuleStatusType.Unknown:
                return 'pi pi-question-circle mz-orange';
        }
    }

    abstract onDestroy(): void;
}
